/**
 * Data layer for `igaming-deposit-stream`. Thin typed fetch wrappers over /api/v1/igaming-deposit-stream.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface IgamingDepositStreamRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIgamingDepositStreamResponse {
	count: number;
	items: IgamingDepositStreamRow[];
}

export interface CreateIgamingDepositStreamBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/igaming-deposit-stream";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIgamingDepositStreamResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIgamingDepositStreamResponse>(res);
}

export async function create(body: CreateIgamingDepositStreamBody): Promise<IgamingDepositStreamRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<IgamingDepositStreamRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const igamingDepositStreamQueryKeys = {
	all: ["igaming-deposit-stream"] as const,
	list: () => [...igamingDepositStreamQueryKeys.all] as const,
	detail: (id: string) => [...igamingDepositStreamQueryKeys.all, id] as const,
};
